import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalInputText } from '../../business/manage/components/Modal';
import { privateContactConfirmationRequest } from '../../../../Networking';
import { ADDRESS_SEARCH_DELAY, searchAddresses, addressLabel, accountAddressFromGazetteer, formatAccountAddress } from '../../components/maaAmetAddress';
import './ContactConfirmation.scss';

// Yearly contact data check for ID credit customers. The backend decides who is asked
// (olerexweb.private.contactConfirmation status: an active ID credit card and nothing
// confirmed within a year); this asks them to review their e-mail and phone and blocks
// self-service until they do. Steps:
//   review     -> e-mail, phone and postal address, editable; a new address is picked from
//                 the Maa-amet address search and saved to the account when the check is done
//   emailCode  -> only if the e-mail changed or was never confirmed: link + code, and a
//                 background check so clicking the link moves the modal on by itself
//   emailDone  -> the e-mail is confirmed; the client continues to the phone themselves
//   phoneCode  -> only if the phone changed or was never confirmed: SMS code
//   confirm    -> saves the check, then a thank-you the client closes; the modal is gone for a year
// The backend skips a step whose data is already confirmed, so an unchanged, confirmed
// client goes from review straight to done.

// How often the e-mail step checks whether the client confirmed through the link.
const EMAIL_STATUS_POLL_INTERVAL = 3000;

class ContactConfirmation extends Component {
    constructor(props) {
        super(props);
        this.state = {
            required: false,
            step: 'review', // review | emailCode | emailDone | phoneCode | done
            email: '',
            phone: '',
            code: '',
            sentTo: '',
            loading: false,
            error: null,
            notice: null,
            // Whether an e-mail or phone was confirmed by code or link during this check.
            changed: false,
            // Whether the phone was confirmed by SMS code, for the closing thank-you to say so.
            phoneChanged: false,
            // The account's postal address (fin_crm_client shape), null when there is none to
            // confirm; addressText is what the field shows, and a typed text that no longer
            // matches the address means a new one has to be picked from the search.
            address: null,
            addressText: '',
            addressSuggestions: [],
        };
    }

    get userId() {
        return this.props.login && this.props.login.currentRole && this.props.login.currentRole.userId;
    }

    componentDidMount() {
        this.loadStatus();
    }

    componentDidUpdate(prevProps) {
        const prevUserId = prevProps.login && prevProps.login.currentRole && prevProps.login.currentRole.userId;
        if (prevUserId !== this.userId) {
            this.loadStatus();
        }
    }

    componentWillUnmount() {
        this.stopPolling();
        clearTimeout(this.addressTimer);
    }

    request(body, success, failure) {
        this.props.contactRequest(
            { userId: this.userId, token: this.props.login.token, ...body },
            data => success(data && data.records && data.records.result),
            failure
        );
    }

    // A failed status check never blocks anyone: the check simply is not shown this time.
    loadStatus() {
        if (!this.userId) {
            this.setState({ required: false });
            return;
        }
        this.request(
            { requestType: 'status', ignoreErrorModal: true },
            result => {
                if (!result || !result.required) {
                    this.setState({ required: false });
                    return;
                }
                this.setState({
                    required: true,
                    step: 'review',
                    email: result.email || '',
                    phone: result.phone || '',
                    address: result.address || null,
                    addressText: formatAccountAddress(result.address),
                    error: null,
                    notice: null,
                });
            },
            () => this.setState({ required: false })
        );
    }

    fail = (result) => {
        this.setState({ loading: false, error: (result && result.message) || this.props.translation.errorText });
    };

    // review -> confirm the e-mail (or skip it when already confirmed) -> phone -> done.
    submitReview = () => {
        const { email, phone, address } = this.state;
        if (!email.trim() || !phone.trim()) {
            this.setState({ error: this.props.translation.fieldsRequired });
            return;
        }
        if (address !== null && !this.addressPicked()) {
            this.setState({ error: this.props.translation.addressPick });
            return;
        }
        this.sendEmailCode(false);
    };

    // The shown address stands only while the field still reads exactly that address; any edit
    // means the client has to pick the new one from the search, so it arrives whole.
    addressPicked() {
        const { address, addressText } = this.state;
        return !!(address && address.street && address.city && address.zip)
            && addressText.trim() === formatAccountAddress(address);
    }

    onAddressChange = (value) => {
        this.setState({ addressText: value, error: null });
        clearTimeout(this.addressTimer);
        if (value.trim().length < 3) {
            this.setState({ addressSuggestions: [] });
            return;
        }
        const query = value.trim();
        this.addressTimer = setTimeout(() => {
            searchAddresses(query)
                // Ignore a response that arrived after the client kept typing.
                .then(addresses => this.state.addressText.trim() === query && this.setState({ addressSuggestions: addresses }))
                .catch(() => this.setState({ addressSuggestions: [] }));
        }, ADDRESS_SEARCH_DELAY);
    };

    pickAddress = (match) => {
        clearTimeout(this.addressTimer);
        const address = accountAddressFromGazetteer(match);
        this.setState({ address, addressText: formatAccountAddress(address), addressSuggestions: [], error: null });
    };

    sendEmailCode = (resend) => {
        this.setState({ loading: true, error: null, notice: null }, () => {
            this.request(
                { requestType: 'sendEmailCode', email: this.state.email.trim(), resend },
                result => {
                    if (result && result.confirmed) {
                        this.setState({ email: result.email }, () => this.sendPhoneCode(false));
                        return;
                    }
                    if (result && result.sent) {
                        this.setState({
                            step: 'emailCode',
                            sentTo: result.email,
                            code: '',
                            loading: false,
                            notice: resend ? this.props.translation.resent : null,
                        }, this.startPolling);
                        return;
                    }
                    this.fail(result);
                },
                () => this.fail()
            );
        });
    };

    confirmEmailCode = () => {
        const code = this.state.code.trim();
        if (!/^\d{5}$/.test(code)) {
            this.setState({ error: this.props.translation.codeInvalid });
            return;
        }
        this.setState({ loading: true, error: null, notice: null }, () => {
            this.request(
                { requestType: 'confirmEmailCode', code },
                result => {
                    if (result && result.confirmed) {
                        this.onEmailConfirmed(result.email);
                        return;
                    }
                    this.fail(result);
                },
                () => this.fail()
            );
        });
    };

    // Show that the e-mail went through before asking for the phone, rather than jumping
    // straight into the SMS step.
    onEmailConfirmed(email) {
        this.stopPolling();
        this.setState({ email, changed: true, step: 'emailDone', loading: false, error: null, notice: null });
    }

    continueToPhone = () => {
        this.sendPhoneCode(false);
    };

    startPolling = () => {
        this.stopPolling();
        this.poll = setInterval(this.pollEmailStatus, EMAIL_STATUS_POLL_INTERVAL);
    };

    stopPolling() {
        clearInterval(this.poll);
        this.poll = null;
    }

    // The client may confirm through the link in another tab or on their phone. A failed
    // check is just tried again.
    pollEmailStatus = () => {
        const email = this.state.sentTo;
        if (this.state.step !== 'emailCode' || this.state.loading || this.pollPending) {
            return;
        }
        this.pollPending = true;
        this.request(
            { requestType: 'emailStatus', email, ignoreErrorModal: true },
            result => {
                this.pollPending = false;
                if (result && result.confirmed && this.state.step === 'emailCode' && this.state.sentTo === email) {
                    this.onEmailConfirmed(email);
                }
            },
            () => {
                this.pollPending = false;
            }
        );
    };

    sendPhoneCode = (resend) => {
        this.setState({ loading: true, error: null, notice: null }, () => {
            this.request(
                { requestType: 'sendPhoneCode', phone: this.state.phone.trim(), resend },
                result => {
                    if (result && result.confirmed) {
                        this.setState({ phone: result.phone }, this.finish);
                        return;
                    }
                    if (result && result.sent) {
                        this.setState({
                            step: 'phoneCode',
                            sentTo: result.phone,
                            code: '',
                            loading: false,
                            notice: resend ? this.props.translation.smsResent : null,
                        });
                        return;
                    }
                    this.fail(result);
                },
                () => this.fail()
            );
        });
    };

    confirmPhoneCode = () => {
        const code = this.state.code.trim();
        if (!/^\d{5}$/.test(code)) {
            this.setState({ error: this.props.translation.codeInvalid });
            return;
        }
        this.setState({ loading: true, error: null, notice: null }, () => {
            this.request(
                { requestType: 'confirmPhoneCode', code },
                result => {
                    if (result && result.confirmed) {
                        this.setState({ phone: result.phone, changed: true, phoneChanged: true }, this.finish);
                        return;
                    }
                    this.fail(result);
                },
                () => this.fail()
            );
        });
    };

    finish = () => {
        this.setState({ loading: true, error: null }, () => {
            this.request(
                { requestType: 'confirm', email: this.state.email, phone: this.state.phone, address: this.state.address },
                result => {
                    if (result && result.confirmed) {
                        this.setState({ step: 'done', loading: false, error: null, notice: null });
                        return;
                    }
                    // The backend holds the same rule; if something did not stick, go back
                    // to the review rather than leave the client on a dead step.
                    this.setState({ step: 'review' }, () => this.fail(result));
                },
                () => this.fail()
            );
        });
    };

    // The modal has no close: "back" only returns from a code step to the review. Once the
    // data is confirmed it closes the thank-you and hands self-service back.
    back = () => {
        if (this.state.step === 'done') {
            // The dashboard behind the modal was loaded before the check - its e-mail banner
            // and contact details still show the old state - so reload when anything changed.
            if (this.state.changed) {
                window.location.reload();
                return;
            }
            this.setState({ required: false });
            return;
        }
        // Nothing to go back to from the review, and the e-mail is already confirmed once
        // the client has seen that it went through.
        if (this.state.step === 'review' || this.state.step === 'emailDone' || this.state.loading) {
            return;
        }
        this.stopPolling();
        this.setState({ step: 'review', code: '', error: null, notice: null });
    };

    renderCodeStep(text, onConfirm, onResend, resendLabel) {
        const t = this.props.translation;
        const { code, loading, notice } = this.state;
        return (
            <div className="contact-confirmation_step">
                <p className="contact-confirmation_text">{text}</p>
                <div className="modal-input-text">
                    <label className="modal-input-text_label" htmlFor="contact-confirmation-code">
                        <span className="modal-input-text_label-left">{t.codeLabel}</span>
                    </label>
                    {/* one-time-code lets phones offer the code straight from the e-mail or SMS. */}
                    <input
                        id="contact-confirmation-code"
                        className="modal-input-text_input contact-confirmation_code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={5}
                        autoFocus
                        value={code}
                        onChange={e => this.setState({ code: e.target.value.replace(/\D/g, ''), error: null })}
                        onKeyDown={e => e.key === 'Enter' && onConfirm()}
                    />
                </div>
                {notice && <p className="contact-confirmation_notice">{notice}</p>}
                <div className="contact-confirmation_actions">
                    <button type="button" className="contact-confirmation_link" disabled={loading} onClick={() => onResend(true)}>
                        {resendLabel}
                    </button>
                    <button type="button" className="contact-confirmation_link" disabled={loading} onClick={this.back}>
                        {t.changeData}
                    </button>
                </div>
            </div>
        );
    }

    renderAddressField() {
        const t = this.props.translation;
        const { addressText, addressSuggestions } = this.state;
        return (
            <div className="modal-input-text contact-confirmation_address">
                <label className="modal-input-text_label" htmlFor="contact-confirmation-address">
                    <span className="modal-input-text_label-left">{t.addressLabel}</span>
                </label>
                <input
                    id="contact-confirmation-address"
                    className="modal-input-text_input"
                    type="text"
                    autoComplete="off"
                    value={addressText}
                    onChange={e => this.onAddressChange(e.target.value)}
                    onBlur={() => setTimeout(() => this.setState({ addressSuggestions: [] }), 150)}
                />
                {addressSuggestions.length > 0 &&
                    <ul className="contact-confirmation_address-list">
                        {addressSuggestions.map(match =>
                            // onMouseDown so the pick lands before the input's blur clears the list.
                            <li
                                key={match.adr_id || match.ads_oid}
                                className="contact-confirmation_address-item"
                                onMouseDown={() => this.pickAddress(match)}
                            >
                                {addressLabel(match)}
                            </li>)}
                    </ul>}
            </div>
        );
    }

    render() {
        const t = this.props.translation;
        const { required, step, email, phone, address, sentTo, loading, error } = this.state;

        if (!required || !t) {
            return null;
        }

        if (step === 'done') {
            return (
                <div className="modal-translucent contact-confirmation contact-confirmation--done">
                    <Modal {...{
                        title: t.doneTitle,
                        onCloseButtonClick: this.back,
                        isDone: true,
                        result: this.state.phoneChanged ? t.donePhoneText : t.doneText,
                        height: 'auto',
                    }} />
                </div>
            );
        }

        let content;
        let action;
        let actionTitle;

        if (step === 'emailDone') {
            content = (
                <div className="contact-confirmation_step">
                    <p className="contact-confirmation_success">{t.emailDoneText(email)}</p>
                </div>
            );
            action = this.continueToPhone;
            actionTitle = t.continueToPhone;
        } else if (step === 'emailCode') {
            content = this.renderCodeStep(t.emailCodeText(sentTo), this.confirmEmailCode, this.sendEmailCode, t.resendEmail);
            action = this.confirmEmailCode;
            actionTitle = t.confirmCode;
        } else if (step === 'phoneCode') {
            content = this.renderCodeStep(t.phoneCodeText(sentTo), this.confirmPhoneCode, this.sendPhoneCode, t.resendSms);
            action = this.confirmPhoneCode;
            actionTitle = t.confirmCode;
        } else {
            content = (
                <div className="contact-confirmation_step">
                    <p className="contact-confirmation_text">{t.reviewText}</p>
                    <div className="contact-confirmation_fields">
                        <ModalInputText {...{
                            id: 'contact-confirmation-email',
                            text: t.emailLabel,
                            value: email,
                            onChange: e => this.setState({ email: e.target.value, error: null }),
                        }} />
                        <ModalInputText {...{
                            id: 'contact-confirmation-phone',
                            text: t.phoneLabel,
                            value: phone,
                            onChange: e => this.setState({ phone: e.target.value, error: null }),
                        }} />
                        {address !== null && this.renderAddressField()}
                    </div>
                    <p className="contact-confirmation_support">{t.support}</p>
                </div>
            );
            action = this.submitReview;
            actionTitle = t.dataCorrect;
        }

        return (
            <div className={`modal-translucent contact-confirmation contact-confirmation--${step}`}>
                <Modal {...{
                    title: step === 'emailDone' ? t.emailDoneTitle : t.title,
                    onCloseButtonClick: this.back,
                    content,
                    isSaveButton: true,
                    actionButtonActive: !loading,
                    onActionButtonClick: action,
                    actionButtonTitle: actionTitle,
                    isLoaderVisible: loading,
                    isError: !!error,
                    result: error,
                    height: 'auto',
                }} />
            </div>
        );
    }
}

export default connect(
    state => ({
        translation: state.translation.contactConfirmation,
        login: state.login,
    }),
    dispatch => ({
        contactRequest: (body, success, error) => privateContactConfirmationRequest(dispatch, body, success, error),
    })
)(ContactConfirmation);
